/**
 * Cart Upsell Button Click Handler
 *
 * Handles upgrade clicks for TeeInBlue customizable products.
 * Swaps a cart item variant while preserving all customizations and addon variants.
 *
 * Based on TeeInBlue API: copies _customization_id, _tib_addon_variant, and all properties
 */

(function() {
  'use strict';

  /**
   * Event delegation for cart upsell button clicks
   * Listens on document level to catch clicks on buttons added via AJAX or initial render
   */
  document.addEventListener('click', function(e) {
    // Check if the clicked element is or is inside an upsell button
    const upsellButton = e.target.closest('.cart-upsell-button .upsell-button');
    if (!upsellButton) return;

    const upsellComponent = e.target.closest('.cart-upsell-button');
    if (!upsellComponent) return;

    e.preventDefault();

    // Prevent double clicks
    if (upsellButton.disabled) return;

    console.log('Cart upsell button clicked');

    // Get data attributes
    const itemKey = upsellComponent.getAttribute('data-cart-item-key');
    const targetVariantId = upsellComponent.getAttribute('data-target-variant-id');
    const matchingCount = parseInt(upsellComponent.getAttribute('data-matching-count') || '1', 10);
    const matchingVariantsRaw = upsellComponent.getAttribute('data-matching-variants');

    if (!itemKey || !targetVariantId) {
      console.error('Missing required data attributes');
      showError(upsellButton, 'Invalid configuration');
      return;
    }

    // Check if there are multiple matching variants - if so, open modal for selection
    if (matchingCount > 1 && matchingVariantsRaw) {
      try {
        const matchingVariants = JSON.parse(matchingVariantsRaw);
        console.log('Multiple variants found:', matchingVariants.length, matchingVariants);

        // Open frame option selector modal
        const modal = document.getElementById('frame-option-selector-modal');
        if (modal && typeof modal.open === 'function') {
          // Get item properties from cart
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              const item = cart.items.find(i => i.key === itemKey);
              const itemProperties = item ? item.properties : {};

              modal.open({
                variants: matchingVariants,
                itemKey: itemKey,
                originalVariantId: parseInt(upsellComponent.getAttribute('data-original-variant-id'), 10),
                itemProperties: itemProperties
              });
            })
            .catch(err => {
              console.error('Failed to fetch cart for modal:', err);
              showError(upsellButton, 'Could not load options');
            });
        } else {
          console.error('Frame option selector modal not found');
          showError(upsellButton, 'Modal not available');
        }
      } catch (err) {
        console.error('Failed to parse matching variants:', err);
        showError(upsellButton, 'Invalid data');
      }
      return;
    }

    // Single variant - proceed directly with upsell
    console.log('Single variant match, proceeding with direct upsell');

    // Show loading state
    setLoadingState(upsellButton, true);

    // Execute upsell operation
    performUpsell(itemKey, parseInt(targetVariantId, 10), upsellButton);
  });

  /**
   * Perform the upsell operation:
   * 1. Fetch cart to get item properties
   * 2. Remove original item
   * 3. Add new variant with copied properties
   * 4. Add addon variants if present
   * 5. Refresh cart UI
   */
  function performUpsell(itemKey, targetVariantId, button) {
    // Step 1: Fetch current cart
    fetch('/cart.js')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch cart');
        return res.json();
      })
      .then(cart => {
        console.log('Cart fetched:', cart);

        // Find the item by key
        const item = cart.items.find(i => i.key === itemKey);
        if (!item) {
          throw new Error('Item not found in cart');
        }

        console.log('Found item to upgrade:', item);

        // Extract all properties
        const itemProperties = item.properties || {};

        // Create new item input with target variant and copied properties
        const newItemInput = {
          id: targetVariantId,
          quantity: item.quantity,
          properties: { ...itemProperties }
        };

        let newItems = [newItemInput];

        // Clone addon variants if present (TeeInBlue specific)
        if (itemProperties._tib_addon_variant) {
          console.log('Cloning addon variants:', itemProperties._tib_addon_variant);

          // _tib_addon_variant format: "{variant_id_1}:{quantity_1}|{variant_id_2}:{quantity_2}"
          const variants = itemProperties._tib_addon_variant
            .split('|')
            .map(v => v.split(':'))
            .map(([id, quantity]) => ({
              id: parseInt(id, 10),
              quantity: parseInt(quantity, 10),
              properties: {
                _tib_customization_id: itemProperties._customization_id,
              }
            }));

          newItems = [...newItems, ...variants];
          console.log('Total items to add (including addons):', newItems.length);
        }

        // Step 2: Remove original item from cart
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0
          })
        })
        .then(res => {
          if (!res.ok) throw new Error('Failed to remove original item');
          return res.json();
        })
        .then(() => {
          console.log('Original item removed');

          // Step 3: Add new items (variant + addons)
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items: newItems })
          });
        });
      })
      .then(res => {
        if (!res.ok) throw new Error('Failed to add upgraded item');
        return res.json();
      })
      .then(async (result) => {
        console.log('Upgraded item added:', result);

        // Step 4: Refresh cart UI
        await refreshCart();

        // Show success state
        setSuccessState(button);

        // Reset button after delay
        setTimeout(() => {
          setLoadingState(button, false);
        }, 2000);
      })
      .catch(error => {
        console.error('Upsell error:', error);
        showError(button, error.message);
        setLoadingState(button, false);
      });
  }

  /**
   * Set loading state on button
   */
  function setLoadingState(button, isLoading) {
    button.disabled = isLoading;

    const textSpan = button.querySelector('.upsell-button-text');
    const loadingSpan = button.querySelector('.upsell-button-loading');
    const iconSpan = button.querySelector('.upsell-button-icon');

    if (isLoading) {
      if (textSpan) textSpan.style.display = 'none';
      if (iconSpan) iconSpan.style.display = 'none';
      if (loadingSpan) loadingSpan.style.display = 'block';
    } else {
      if (textSpan) textSpan.style.display = 'block';
      if (iconSpan) iconSpan.style.display = 'block';
      if (loadingSpan) loadingSpan.style.display = 'none';
    }
  }

  /**
   * Show success state on button
   */
  function setSuccessState(button) {
    const textSpan = button.querySelector('.upsell-button-text');
    if (textSpan) {
      const originalText = textSpan.textContent;
      textSpan.textContent = '✓ Upgraded!';

      setTimeout(() => {
        textSpan.textContent = originalText;
      }, 2000);
    }
  }

  /**
   * Show error message
   */
  function showError(button, message) {
    const textSpan = button.querySelector('.upsell-button-text');
    if (textSpan) {
      const originalText = textSpan.textContent;
      textSpan.textContent = '✗ ' + message;
      button.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';

      setTimeout(() => {
        textSpan.textContent = originalText;
        button.style.background = '';
      }, 3000);
    }
  }

  /**
   * Refresh cart UI using Shopify section rendering
   * Triggers cart update event that other components listen for
   */
  async function refreshCart() {
    try {
      // Fetch updated cart and dispatch event
      const cart = await fetch('/cart.js').then(res => res.json());

      console.log('Cart refreshed');

      // Dispatch cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: { cart }
      }));

      // Refresh cart drawer using section rendering (preserves component state)
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh();
      }
    } catch (err) {
      console.error('Failed to refresh cart:', err);
    }
  }

  // Register custom element
  if (!customElements.get('cart-upsell-button')) {
    class CartUpsellButton extends HTMLElement {
      constructor() {
        super();
      }
    }
    customElements.define('cart-upsell-button', CartUpsellButton);
  }

  console.log('Cart upsell handler initialized');
})();
